/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useState } from 'react';
import {
	BUTTON,
	DROPDOWN_ITEM,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import {
	CUSTOM_ITEM_DROPDOWN_ACTION,
	FIXED_ITEM_BUTTON,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { ALERT_SETTINGS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-id.tsx';
import {
	LEVEL_FOUR,
	LEVEL_THREE,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { MENU_ID_OPERATIONS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import type { OnCloseProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { useStopPropagation } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-stop-propagation/index.tsx';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import type { MoreNavMenuButtonMenuItemsProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/submenu/index.tsx';
import FavoriteButtonStateless from '@jira/platform__favouriting-favourite-button-stateless/src/view/view.tsx';
import { useOgTenantInfo } from '@jira/eoc__tenant-info/src/controllers/index.tsx';
import { token } from '@atlaskit/tokens';
import ButtonItem from '@atlaskit/menu/button-item';
import Section from '@atlaskit/menu/section';
import TrashIcon from '@atlaskit/icon/core/delete';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { toFlagId, useFlagsService } from '@atlassian/jira-flags';
import { deleteSearchEndpoint } from '@atlassian/eoc-alert-list/services/saved-searches/endpoints';
import { useSavedSearchRelayUtils } from '@atlassian/eoc-alert-list/saved-search-relay-utils';
import { DeleteModal } from '@atlassian/eoc-alert-common/delete-modal';
import { OpsgenieSavedSearchAri } from '@atlassian/ari/opsgenie/saved-search';
import { FETCH_ERROR_FLAG_ID } from '../../../../common/constants.tsx';
import { useStarredSearchService } from '../../../../service/use-starred-search/useStarredSearchService.tsx';
import type { AlertActionProps, ModalType } from './types.tsx';

const deleteSavedSearchMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.alerts-content.alerts-menu-item.action-button.delete-saved-search',
	defaultMessage: 'Delete',
	description:
		"Button text displayed in the alerts menu item action area. When clicked, permanently removes the saved search from the user's list.",
});

const confirmTrashHeading = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.alerts-content.alerts-menu-item.action-button.confirm-trash-heading',
	defaultMessage: 'Delete saved search',
	description:
		'Modal title displayed at the top of the confirmation dialog. Appears when user clicks the delete button for a saved search.',
});

const confirmTrashMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.alerts-content.alerts-menu-item.action-button.confirm-trash-message',
	defaultMessage: 'Are you sure you want to delete this saved search?',
	description:
		'Confirmation text displayed in the delete modal body. Shows the user the consequences of proceeding with the deletion action.',
});

const errorTitle = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.alerts-content.alerts-menu-item.action-button.error-title',
	defaultMessage: 'Error',
	description:
		'Title displayed in an error toast notification. Appears when a saved search deletion operation fails due to server or network issues.',
});

const errorMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.alerts-content.alerts-menu-item.action-button.error-message',
	defaultMessage: 'Something went wrong. Please try again.',
	description:
		'Body text displayed in an error toast notification. Appears when a saved search deletion operation fails and provides user guidance.',
});

const deleteModalMessageDescriptors = {
	title: confirmTrashHeading,
	description: confirmTrashMessage,
};

export const SavedSearchActionButtons = ({
	onClose,
	savedSearch,
	savedSearchType,
	isStarred = false,
}: AlertActionProps &
	OnCloseProps & {
		savedSearchType?: 'default' | 'sharedWithMe' | 'createdByMe';
		isStarred?: boolean;
	}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { starSavedSearch, unstarSavedSearch } = useStarredSearchService();
	const { isOpsAdmin } = useOgTenantInfo();

	const handleNavMenuOpen = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: BUTTON,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: {
				componentType: FIXED_ITEM_BUTTON,
				level: LEVEL_THREE,
				itemId: ALERT_SETTINGS,
				parentItemType: 'savedSearch',
				parentItemId: savedSearch.id,
			},
		});
	}, [createAnalyticsEvent, savedSearch.id]);

	const MenuItems = useCallback(
		(props: MoreNavMenuButtonMenuItemsProps) => (
			<MoreMenuItems
				onClose={props.onClose}
				savedSearch={savedSearch}
				isStarred={isStarred}
				savedSearchType={savedSearchType}
			/>
		),
		[savedSearch, isStarred, savedSearchType],
	);

	const canDelete = savedSearchType === 'createdByMe' || isOpsAdmin;

	return (
		<>
			<FavoriteButtonStateless
				isFavorite={isStarred}
				isSmall
				onClick={() => {
					if (isStarred) {
						unstarSavedSearch(savedSearch);
					} else {
						starSavedSearch(savedSearch);
					}
				}}
			/>
			{canDelete && (
				<MoreNavMenuButton
					onOpen={handleNavMenuOpen}
					MenuItems={MenuItems}
					onClose={onClose}
					name={savedSearch.name}
				/>
			)}
		</>
	);
};

const MoreMenuItems = ({
	onClose,
	savedSearch,
	isStarred = false,
	savedSearchType,
}: AlertActionProps &
	MoreNavMenuButtonMenuItemsProps & {
		isStarred?: boolean;
		savedSearchType?: 'default' | 'sharedWithMe' | 'createdByMe';
	}) => {
	const { formatMessage } = useIntl();
	const [modalType, setModalType] = useState<ModalType | undefined>(undefined);
	const onCloseWithStopPropagation = useStopPropagation(onClose);
	const { removeSavedSearchFromCreatedByMe, removeSavedSearchFromSharedWithMe } =
		useSavedSearchRelayUtils();
	const { showFlag } = useFlagsService();
	const [isLoading, setIsLoading] = useState(false);
	const { removeStarredSearchFromCache } = useStarredSearchService();

	const deleteSavedSearch = useCallback(
		(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
			setIsLoading(true);
			const savedSearchId = OpsgenieSavedSearchAri.parse(savedSearch.id).savedSearchId;

			deleteSearchEndpoint(savedSearchId)
				.then(() => {
					if (savedSearchType === 'sharedWithMe') {
						removeSavedSearchFromSharedWithMe(savedSearch.id);
					} else {
						removeSavedSearchFromCreatedByMe(savedSearch.id);
					}
					if (isStarred) {
						removeStarredSearchFromCache(savedSearch.id);
					}
				})
				.catch(() => {
					showFlag({
						key: toFlagId(FETCH_ERROR_FLAG_ID),
						type: 'error',
						title: errorTitle,
						description: errorMessage,
						// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
						messageId:
							'navigation-apps-sidebar-nav4-sidebars-content-operations.ui.alerts-content.alerts-menu-item.action-button.show-flag.error',
						messageType: 'transactional',
					});
				})
				.finally(() => {
					setIsLoading(false);
					if (onClose) {
						onClose(e);
					}
				});
		},
		[
			savedSearch,
			onClose,
			removeSavedSearchFromCreatedByMe,
			removeSavedSearchFromSharedWithMe,
			savedSearchType,
			showFlag,
			removeStarredSearchFromCache,
			isStarred,
		],
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>) => {
			event.preventDefault();
			setModalType('delete');

			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'clicked',
				actionSubject: DROPDOWN_ITEM,
				actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
				attributes: {
					componentType: CUSTOM_ITEM_DROPDOWN_ACTION,
					level: LEVEL_FOUR,
					itemId: 'deleteSavedSearch',
					parentItemType: 'savedSearch',
					parentItemId: savedSearch.id,
				},
			});
		},
		[createAnalyticsEvent, savedSearch.id],
	);

	return (
		<>
			<Section>
				<ButtonItem
					iconBefore={<TrashIcon color={token('color.icon.danger')} label="" />}
					onClick={handleClick}
				>
					{formatMessage(deleteSavedSearchMessage)}
				</ButtonItem>
			</Section>
			<DeleteModal
				messageDescriptors={deleteModalMessageDescriptors}
				isDeleteDialogOpen={modalType === 'delete'}
				onDeleteAttemptCancel={onCloseWithStopPropagation}
				isLoading={isLoading}
				onDelete={deleteSavedSearch}
			/>
		</>
	);
};
